// ============================================================
// Onglet « Crashs » : libellés, couleurs, dates et badge, en fonctions pures (testées par
// crashes.model.test.ts). Les heures sont celles du fuseau du navigateur ; `timeZone` ne sert
// qu'aux tests.
// ============================================================

import type {
  CrashBadgeSummary, CrashKind, CrashLine, CrashOutcome, CrashSummary, WatchStateCode,
} from '../../services/crash-watch.service';

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

export const KIND_META: Record<CrashKind, { label: string; tone: Tone; hint: string }> = {
  crash: { label: 'Crash', tone: 'danger', hint: 'Le serveur s\'est arrêté seul, sans que personne ne le demande.' },
  watchdog: { label: 'Gel (Watchdog)', tone: 'danger', hint: 'Le Watchdog a redémarré le serveur : un monde ne répondait plus, ou la mémoire était presque pleine.' },
  oom: { label: 'Mémoire pleine', tone: 'danger', hint: 'Le processus a été arrêté faute de mémoire.' },
  coupure: { label: 'Redémarrage inexpliqué', tone: 'warning', hint: 'Le serveur a redémarré pendant que la surveillance ne recevait plus rien : la raison n\'a pas pu être lue.' },
  'arret-propre': { label: 'Arrêt propre', tone: 'muted', hint: 'Arrêt décidé par le serveur lui-même (redémarrage planifié, commande stop en jeu) : pas compté comme un crash.' },
};

export const OUTCOME_META: Record<CrashOutcome, { label: string; tone: Tone }> = {
  redemarre: { label: 'Relancé', tone: 'success' },
  arrete: { label: 'Resté arrêté', tone: 'danger' },
  'relance-ratee': { label: 'La relance a planté aussi', tone: 'danger' },
  inconnu: { label: 'Suite inconnue', tone: 'muted' },
};

export function kindMeta(kind: string): { label: string; tone: Tone; hint: string } {
  return KIND_META[kind as CrashKind] ?? { label: kind || 'Arrêt', tone: 'warning', hint: '' };
}

export function outcomeMeta(outcome: string): { label: string; tone: Tone } {
  return OUTCOME_META[outcome as CrashOutcome] ?? { label: outcome || '?', tone: 'muted' };
}

export function watchTone(state: WatchStateCode | string | undefined): Tone {
  switch (state) {
    case 'active': return 'success';
    case 'reconnecting':
    case 'pending': return 'warning';
    default: return 'muted';
  }
}

export interface TabBadge {
  count: number;
  tone: 'danger' | 'warning';
  title: string;
}

/** Badge de l'onglet : crashs des dernières 24 h (rouge), sinon nouvelles erreurs (orange), sinon rien. */
export function tabBadge(s: Pick<CrashBadgeSummary, 'crashes24h' | 'newErrors24h'> | null | undefined): TabBadge | null {
  if (!s) return null;
  const crashes = s.crashes24h ?? 0;
  const errors = s.newErrors24h ?? 0;
  if (crashes + errors <= 0) return null;
  const parts: string[] = [];
  if (crashes > 0) parts.push(`${crashes} crash${crashes > 1 ? 's' : ''}`);
  if (errors > 0) parts.push(`${errors} nouvelle${errors > 1 ? 's' : ''} erreur${errors > 1 ? 's' : ''}`);
  return {
    count: crashes > 0 ? crashes : errors,
    tone: crashes > 0 ? 'danger' : 'warning',
    title: `${parts.join(' et ')} depuis 24 h`,
  };
}

const NNBSP = ' ';

/** « 1 234 » (espace fine insécable, comme fr-FR). */
export function fmtCount(n: number): string {
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, NNBSP);
}

function parts(d: Date, timeZone?: string) {
  const f = new Intl.DateTimeFormat('fr-FR', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(d)) p[x.type] = x.value;
  return p;
}

/** « aujourd'hui 14:10:03 », « hier 22:01:44 », « 18/10 10:00:12 ». */
export function fmtWhen(iso: string | null | undefined, now: Date = new Date(), timeZone?: string): string {
  if (!iso) return '?';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '?';
  const p = parts(d, timeZone);
  const today = parts(now, timeZone);
  const yesterday = parts(new Date(now.getTime() - 86_400_000), timeZone);
  const time = `${p.hour}:${p.minute}:${p.second}`;
  const same = (q: Record<string, string>) => q.year === p.year && q.month === p.month && q.day === p.day;
  if (same(today)) return `aujourd'hui ${time}`;
  if (same(yesterday)) return `hier ${time}`;
  return `${p.day}/${p.month} ${time}`;
}

/** « Exemple d'aujourd'hui 14:10:03 », « Exemple d'hier 22:01:44 », « Exemple du 18/10 10:00:12 ». */
export function exampleLabel(iso: string | null | undefined, now: Date = new Date(), timeZone?: string): string {
  const when = fmtWhen(iso, now, timeZone);
  if (when.startsWith('aujourd\'hui') || when.startsWith('hier')) return `Exemple d'${when}`;
  return `Exemple du ${when}`;
}

/** « à l'instant », « il y a 5 min », « il y a 3 h », « il y a 2 j ». */
export function fmtAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '?';
  const ms = now.getTime() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '?';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'à l\'instant';
  if (min < 60) return `il y a ${min}${NNBSP}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}${NNBSP}h`;
  return `il y a ${Math.floor(h / 24)}${NNBSP}j`;
}

const STATE_FR: Record<string, string> = {
  running: 'en marche', starting: 'en démarrage', stopping: 'en arrêt', offline: 'arrêté',
};

/** Faits courts sous la cause : état d'avant, code de sortie, mémoire, crashs de plus. */
export function crashFacts(c: CrashSummary): string[] {
  const out: string[] = [];
  if (c.previousState) out.push(`Avant l'arrêt : ${STATE_FR[c.previousState] ?? c.previousState}`);
  if (c.exitCode != null) out.push(`Code de sortie ${c.exitCode}`);
  if (c.outOfMemory) out.push('Mémoire saturée (signalé par Pterodactyl)');
  else if (c.memoryPercent != null) out.push(`Mémoire ${Math.round(c.memoryPercent)}${NNBSP}% juste avant`);
  if (c.repeats > 0) out.push(`+${c.repeats} crash${c.repeats > 1 ? 's' : ''} de plus dans l'heure (non détaillés)`);
  return out;
}

export type LineTone = 'cause' | 'error' | 'daemon' | 'after' | 'normal';

const LEVEL_RE = /\[(?:[^\]]*\s)?(?:error|fatal|critical|severe|err|ftl|crit)\]/i;
const ERROR_RE = /\/(?:ERROR|FATAL|SEVERE)\]|Unhandled [Ee]xception|Exception in thread|^\s*(?:[A-Za-z_][\w$`]*\.)+[A-Z][\w$`]*(?:Exception|Error)(?::\s.*)?$/;
const FRAME_RE = /^\s*(?:at\s+\S|---\s*End of|--->|Caused by:|\.\.\.\s*\d+\s+more)/;

/** Couleur d'une ligne d'un rapport : la ligne de la cause et sa pile, les erreurs, les messages de Wings. */
export function lineTones(lines: CrashLine[], causeIndex: number): LineTone[] {
  const tones: LineTone[] = lines.map((l) => {
    if (l.text.startsWith('[Pterodactyl]') || /\[[^\]]*Daemon\]/.test(l.text)) return 'daemon';
    if (LEVEL_RE.test(l.text) || ERROR_RE.test(l.text)) return 'error';
    return l.after ? 'after' : 'normal';
  });
  if (causeIndex >= 0 && causeIndex < lines.length) {
    tones[causeIndex] = 'cause';
    for (let i = causeIndex + 1; i < lines.length && FRAME_RE.test(lines[i].text); i++) tones[i] = 'cause';
  }
  return tones;
}

/**
 * Dossier du rapport complet du Watchdog cité dans les lignes (« report: /home/container/…/x.json »),
 * en chemin de l'onglet Files (sa racine est /home/container), ou null.
 */
export function watchdogReportFolder(lines: Array<{ text: string }>): string | null {
  for (let i = lines.length - 1; i >= 0; i--) {
    const m = /report:\s*(\/\S+\.json)/.exec(lines[i].text);
    if (!m) continue;
    const root = ['/home/container', '/mcserv'].find((r) => m[1].startsWith(r + '/'));
    // Hors du dossier du serveur : l'onglet Files ne peut pas l'ouvrir
    if (!root) return null;
    const p = m[1].slice(root.length);
    const dir = p.slice(0, p.lastIndexOf('/'));
    return dir || '/';
  }
  return null;
}
